import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  TextField,
  TablePagination,
  IconButton,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Alert,
  Stack, // Import Stack for better layout control
  Chip, // Import Chip for status badges
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { _get, _delete } from "../../Config/axiosInstance";

const CustomerList = () => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const fetchCustomers = async () => {
    try {
      const response = await _get("getcustomerdetails");
      const data = response.data.data || [];
      setCustomers(data);
      setFilteredCustomers(data);
    } catch (error) {
      console.error("Failed to fetch customers:", error);
      setSnackbarMessage("Failed to fetch customers.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    const filtered = customers.filter((cust) =>
      [cust.custName, cust.custEmail, cust.custMobile, cust.status] // Include status in search
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
    setFilteredCustomers(filtered);
    setPage(0); // Reset to first page on new search
  }, [search, customers]);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteClick = (customerId) => {
    setSelectedCustomerId(customerId);
    setOpenConfirm(true);
  };

  const handleConfirmDelete = async () => {
    setOpenConfirm(false);
    if (selectedCustomerId) {
      try {
        await _delete(`deletecustomer/${selectedCustomerId}`);
        setSnackbarMessage("Customer deleted successfully!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        fetchCustomers(); // Refresh the list
      } catch (error) {
        console.error("Failed to delete customer:", error);
        setSnackbarMessage("Failed to delete customer.");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      } finally {
        setSelectedCustomerId(null);
      }
    }
  };

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
    setSelectedCustomerId(null);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p:1 }}> {/* Max width and center */}
      <Stack direction="column" spacing={3}> {/* Use Stack for vertical spacing */}
        <Typography variant="h4" component="h4" gutterBottom align="left" sx={{ fontWeight: 'normal', color: 'primary.main' }}>
          Customer List
        </Typography>

        <TextField
          label="Search by name, email, phone, or status"
          variant="outlined"
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} // Rounded corners
        />

        <TableContainer component={Paper} elevation={5} sx={{ borderRadius: 2, overflow: 'hidden' }}> 
          <Table stickyHeader aria-label="customer table">
            <TableHead>
              <TableRow sx={{ '& th': { backgroundColor: '#aaa', color: 'primary.contrastText', fontWeight: 'bold' } }}> 
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>DOB</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCustomers.length > 0 ? (
                filteredCustomers
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((customer, index) => (
                    <TableRow
                      key={customer.custId || index}
                      sx={{
                        backgroundColor:
                          customer.status === "Inactive" ? "#fde7e7" : "#fff",
                        "&:nth-of-type(odd)": {
                          backgroundColor:
                            customer.status === "Inactive" ? "#fceaea" : "#fff", 
                        },
                        "&:hover": {
                          backgroundColor:
                            customer.status === "Inactive"
                              ? "#fcd4d4"
                              : "#d4fcd4", 
                          cursor: 'pointer',
                        },
                      }}
                    >
                      <TableCell>{customer.custName}</TableCell>
                      <TableCell>{customer.custEmail}</TableCell>
                      <TableCell>{customer.custMobile}</TableCell>
                      <TableCell>{customer.custAddress}</TableCell>
                      <TableCell>{customer.custDOB}</TableCell>
                      <TableCell>
                        <Chip
                          label={customer.status}
                          color={customer.status === "Active" ? "success" : "error"}
                          size="small"
                          sx={{ fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          aria-label="delete"
                          color="error"
                          onClick={() => handleDeleteClick(customer.custId)}
                          sx={{ '&:hover': { backgroundColor: 'error.light', color: 'white' } }} 
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                    <Typography variant="subtitle1">No customers found matching your search.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={filteredCustomers.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          sx={{ mt: 2 }}
        />
      </Stack>

      {/* Confirmation Dialog */}
      <Dialog
        open={openConfirm}
        onClose={handleCloseConfirm}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title" sx={{ color: "error.main" }}>
          {"Confirm Deletion"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this customer? This action cannot be
            undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm} color="primary">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerList;